import { useState, useEffect, useCallback } from "react";

const COMPARE_KEY = "directproprietaire_compare";
const COMPARE_EVENT = "directproprietaire_compare_change";
const MAX_COMPARE_ITEMS = 3;

// Helper pour lire la liste depuis localStorage
const getCompareListFromStorage = (): string[] => {
  const stored = localStorage.getItem(COMPARE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored).slice(0, MAX_COMPARE_ITEMS);
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Hook pour gérer la comparaison de biens (max 3)
 * Stocke les IDs des annonces dans localStorage
 * Synchronise entre composants via CustomEvent
 */
export const useCompare = () => {
  const [compareList, setCompareList] = useState<string[]>(getCompareListFromStorage);

  // Synchronisation entre onglets (StorageEvent) et entre composants du même onglet (CustomEvent)
  useEffect(() => {
    // Gestionnaire pour les autres onglets
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === COMPARE_KEY) {
        setCompareList(getCompareListFromStorage());
      }
    };

    // Gestionnaire pour les composants du même onglet
    const handleCompareChange = () => {
      setCompareList(getCompareListFromStorage());
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(COMPARE_EVENT, handleCompareChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(COMPARE_EVENT, handleCompareChange);
    };
  }, []);

  // Sauvegarde dans localStorage et notifie les autres composants
  const saveCompareList = useCallback((newList: string[]) => {
    localStorage.setItem(COMPARE_KEY, JSON.stringify(newList));
    setCompareList(newList);
    // Déclenche un événement pour notifier les autres composants du même onglet
    window.dispatchEvent(new CustomEvent(COMPARE_EVENT));
  }, []);

  // Ajoute ou retire une annonce de la comparaison
  const toggleCompare = useCallback(
    (annonceId: string): { added: boolean; error?: string } => {
      const isInList = compareList.includes(annonceId);

      if (isInList) {
        // Retirer de la liste
        const newList = compareList.filter((id) => id !== annonceId);
        saveCompareList(newList);
        return { added: false };
      } else {
        // Vérifier la limite
        if (compareList.length >= MAX_COMPARE_ITEMS) {
          return {
            added: false,
            error: `Vous ne pouvez comparer que ${MAX_COMPARE_ITEMS} biens maximum`
          };
        }
        // Ajouter à la liste
        const newList = [...compareList, annonceId];
        saveCompareList(newList);
        return { added: true };
      }
    },
    [compareList, saveCompareList]
  );

  // Vérifie si une annonce est dans la liste
  const isInCompare = useCallback(
    (annonceId: string) => compareList.includes(annonceId),
    [compareList]
  );

  // Vide la liste de comparaison
  const clearCompare = useCallback(() => {
    saveCompareList([]);
  }, [saveCompareList]);

  // Retire un élément spécifique
  const removeFromCompare = useCallback(
    (annonceId: string) => {
      const newList = compareList.filter((id) => id !== annonceId);
      saveCompareList(newList);
    },
    [compareList, saveCompareList]
  );

  return {
    compareList,
    compareCount: compareList.length,
    maxItems: MAX_COMPARE_ITEMS,
    canAddMore: compareList.length < MAX_COMPARE_ITEMS,
    toggleCompare,
    isInCompare,
    clearCompare,
    removeFromCompare,
  };
};

export default useCompare;
